<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar" />

    <Sidebar
      class="alice-shell__sidebar"
      variant="rail"
      active-key="projects"
    />

    <main class="alice-shell__main">
      <PageHeader
        title="Projects"
        :count="projects.length"
        subtitle="Alle UX-onderzoeken die op dit moment lopen of in concept staan. Filter op klant, methode of eigenaar."
      />

      <StatsStrip :projects="normalizedProjects" />

      <Toolbar
        v-model="query"
        class="alice-shell__toolbar"
        :sort="sort"
        :view="view"
        :count="filtered.length"
        :can-create="!$store.getters.isGuest"
        @update:sort="sort = $event"
        @update:view="view = $event"
        @new-project="openNew"
      />

      <div class="alice-shell__list">
        <template v-if="view === 'rows'">
          <ProjectRowsHeader />
          <EmptyState v-if="filtered.length === 0" :query="query" />
          <ProjectRow
            v-for="p in filtered"
            :key="p.uid"
            :project="p"
            density="cozy"
            @open="openProject"
          />
        </template>
        <template v-else>
          <EmptyState v-if="filtered.length === 0" :query="query" />
          <div v-else class="alice-shell__grid">
            <ProjectCard
              v-for="p in filtered"
              :key="p.uid"
              :project="p"
              @open="openProject"
            />
          </div>
        </template>
      </div>
    </main>

    <v-dialog v-model="newDialog" max-width="420">
      <v-card class="alice-new-dialog">
        <div class="alice-new-dialog__title">New project</div>
        <v-text-field
          v-model="newTitle"
          placeholder="Project title"
          variant="outlined"
          autofocus
          hide-details
          class="alice-new-dialog__input"
        />
        <ClientPicker
          v-model="newClientId"
          :client-name="newClientName"
          placeholder="Select or create a client"
          class="alice-new-dialog__input"
          @update:client-name="newClientName = $event"
        />
        <JobNumberPicker
          v-model="newJobNumber"
          :client-id="newClientId"
          :client-name="newClientName"
          :hint="newJobHint"
          placeholder="Jobnummer (optioneel), bijv. D2303"
          look="dialog"
          class="alice-new-dialog__input"
          @pick="newJobRow = $event"
        />
        <div class="alice-new-dialog__actions">
          <v-btn variant="text" @click="newDialog = false">Cancel</v-btn>
          <v-btn
            color="black"
            :loading="creating"
            :disabled="!newTitle || Boolean(newJobError)"
            @click="addProject"
            >Create</v-btn
          >
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="7000"
      location="bottom right"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { doc, addDoc, updateDoc, collection } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import PageHeader from "@/components/alice/PageHeader.vue";
import StatsStrip from "@/components/alice/StatsStrip.vue";
import Toolbar from "@/components/alice/Toolbar.vue";
import ProjectRowsHeader from "@/components/alice/ProjectRowsHeader.vue";
import ProjectRow from "@/components/alice/ProjectRow.vue";
import ProjectCard from "@/components/alice/ProjectCard.vue";
import EmptyState from "@/components/alice/EmptyState.vue";
import ClientPicker from "@/components/alice/ClientPicker.vue";
import JobNumberPicker from "@/components/alice/JobNumberPicker.vue";
import { normalizeProject } from "@/components/alice/normalize";
import {
  jobNumberError,
  jobRowDetail,
  normaliseJobNumber,
  pickedKey,
} from "@/utils/jobNumber";

export default {
  name: "ProjectsPage",
  components: {
    TopBar,
    Sidebar,
    PageHeader,
    StatsStrip,
    Toolbar,
    ProjectRowsHeader,
    ProjectRow,
    ProjectCard,
    EmptyState,
    ClientPicker,
    JobNumberPicker,
  },
  data() {
    return {
      query: "",
      sort: "recent",
      view: "rows",
      newDialog: false,
      newTitle: "",
      newClientId: null,
      newClientName: "",
      // Optional: the Valsplat job number, typed or picked from the client's
      // Productive jobs (the picked row carries its Productive key).
      newJobNumber: "",
      newJobRow: null,
      creating: false,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  beforeUnmount() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
  },
  computed: {
    ...mapState({
      projects: (s) => s.projects,
      user: (s) => s.user,
      clients: (s) => s.clients,
    }),
    normalizedProjects() {
      return this.projects.map(normalizeProject);
    },
    newJobError() {
      return jobNumberError(this.newJobNumber);
    },
    // The picked job's name under the field, while it still holds its number.
    newJobHint() {
      const row = this.newJobRow;
      if (!row || !pickedKey(row, this.newJobNumber)) return "";
      return [row.name, jobRowDetail(row)].filter(Boolean).join(" · ");
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      let list = this.normalizedProjects;
      if (q) {
        list = list.filter((p) => {
          const owner = p.owner ? p.owner.name.toLowerCase() : "";
          return (
            p.title.toLowerCase().includes(q) ||
            p.client.toLowerCase().includes(q) ||
            (p.method && p.method.toLowerCase().includes(q)) ||
            owner.includes(q)
          );
        });
      }
      if (this.sort === "alpha") {
        list = [...list].sort((a, b) => a.title.localeCompare(b.title));
      } else {
        list = [...list].sort((a, b) => {
          const at = a.created ? a.created.getTime() : 0;
          const bt = b.created ? b.created.getTime() : 0;
          return bt - at;
        });
      }
      return list;
    },
  },
  watch: {
    query() {
      if (this.queryTimer) clearTimeout(this.queryTimer);
      this.queryTimer = setTimeout(() => this.syncUrl(), 250);
    },
    sort() {
      this.syncUrl();
    },
    view() {
      this.syncUrl();
    },
  },
  created() {
    const { q, sort, view } = this.$route.query;
    if (typeof q === "string") this.query = q;
    if (sort === "alpha" || sort === "recent") this.sort = sort;
    if (view === "rows" || view === "grid") this.view = view;
    // Guests can't read the whole projects/clients collections — their list
    // comes from fetchGuestProjects (dispatched in setUser). (fetchProjects/
    // fetchClients are also hardened against a denied read, covering the race
    // where setUser hasn't set the guest flag yet on a direct reload.)
    if (this.$store.getters.isGuest) {
      if (this.projects.length === 0) {
        this.$store.dispatch("fetchGuestProjects");
      }
      return;
    }
    if (this.projects.length === 0) {
      this.$store.dispatch("fetchProjects");
    }
    if (this.$store.state.clients.length === 0) {
      this.$store.dispatch("fetchClients");
    }
  },
  methods: {
    openProject(p) {
      this.$router.push(`/project/${p.uid}/details`);
    },
    openNew() {
      this.newTitle = "";
      this.newClientId = null;
      this.newClientName = "";
      this.newJobNumber = "";
      this.newJobRow = null;
      this.newDialog = true;
    },
    // The job number goes through setProjectJobNumber: it stores the
    // Productive record with it and links the job's Drive folder. The project
    // exists either way, so a failure here only costs the link.
    async saveNewJobNumber(projectId, { code, clientId, clientName }) {
      try {
        const call = httpsCallable(functions, "setProjectJobNumber", {
          timeout: 60000,
        });
        const res = await call({
          projectId,
          jobNumber: code,
          productiveKey: pickedKey(this.newJobRow, code),
          clientId: clientId || null,
          clientName: clientName || null,
        });
        this.snackbar = {
          show: true,
          color: "success",
          text: `Project aangemaakt. ${res.data.message}`,
        };
      } catch (err) {
        console.error("setProjectJobNumber failed:", err);
        this.snackbar = {
          show: true,
          color: "error",
          text: `Project aangemaakt met jobnummer ${code}, maar koppelen aan Productive en Drive is niet gelukt. Sla het jobnummer opnieuw op bij Details.`,
        };
      }
    },
    currentUserAsResearcher() {
      const u = this.user || {};
      const name = u.displayName || u.email || "";
      if (!name) return null;
      return {
        uid: u.uid || null,
        name,
        email: u.email || null,
        photo: u.photoURL || null,
      };
    },
    async addProject() {
      if (!this.newTitle || this.newJobError) return;
      const code = normaliseJobNumber(this.newJobNumber);
      this.creating = true;
      try {
        // Resolve the client once, on this explicit submit. If a new name was
        // typed (no id selected), reuse an existing client case-insensitively or
        // create exactly one — never one per keystroke.
        const clientName = (this.newClientName || "").trim();
        let clientId = this.newClientId || null;
        if (!clientId && clientName) {
          const existing = this.clients.find(
            (c) => (c.name || "").toLowerCase() === clientName.toLowerCase()
          );
          clientId = existing
            ? existing.uid
            : await this.$store.dispatch("createClient", { name: clientName });
        }
        const researcher = this.currentUserAsResearcher();
        const docRef = await addDoc(collection(db, "projects"), {
          title: this.newTitle,
          client_name: clientName || null,
          client_id: clientId,
          report_title: null,
          report_subtitle: null,
          device: null,
          method_description: null,
          n: null,
          participants_description: null,
          test_material: null,
          researchers: researcher ? [researcher] : [],
          context: null,
          main_research_question: null,
          sub_research_questions: [{ text: "" }],
          recommendations: [],
          recommendations_title: null,
          next_steps: [],
          chat_context: null,
          summary: { summaries: [], opportunities: [], title: null },
          status: "draft",
          // Optional; setProjectJobNumber below adds its Productive record
          // and Drive link. Written here too, so it is kept if that fails.
          ...(code ? { project_code: code } : {}),
          createdAt: Math.floor(Date.now() / 1000),
        });
        await updateDoc(doc(db, "projects", docRef.id), { uid: docRef.id });
        if (code) {
          await this.saveNewJobNumber(docRef.id, {
            code,
            clientId,
            clientName,
          });
        }
        await this.$store.dispatch("fetchProjects");
        this.newDialog = false;
      } finally {
        this.creating = false;
      }
    },
    syncUrl() {
      const next = {};
      if (this.query) next.q = this.query;
      if (this.sort !== "recent") next.sort = this.sort;
      if (this.view !== "rows") next.view = this.view;
      const current = this.$route.query;
      const same =
        next.q === current.q &&
        next.sort === current.sort &&
        next.view === current.view;
      if (!same) this.$router.replace({ query: next });
    },
  },
};
</script>

<style scoped>
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
/* The header scrolls away with the list; only the toolbar stays in view. */
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
}
.alice-shell__main > * {
  flex-shrink: 0;
}
.alice-shell__toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
}
.alice-shell__list {
  flex-grow: 1;
  background: var(--vp-white);
}
/* The rows header sticks just below the toolbar (56px tall). */
.alice-shell__list .alice-rows-header {
  top: 56px;
}
.alice-shell__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding: 24px;
}

.alice-new-dialog {
  padding: 24px;
  border-radius: 0 !important;
  font-family: var(--font-body);
}
.alice-new-dialog__title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--vp-black);
}
.alice-new-dialog__input {
  margin-bottom: 16px;
}
.alice-new-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
